import cx from 'clsx';
import { debounce } from 'es-toolkit';
import React from 'react';
import type { RegisterOptions } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';

type Props = {
  children?: React.ReactNode;
  className?: string;
  name: string;
  label?: string;
  placeholder?: string;
  description?: React.ReactNode;
  registerOptions?: RegisterOptions;
  handleErrorMessages?: boolean;
  debouncedAutoSubmitForm?: string;
  debouncedAutoSubmitTimeout?: number;
  rows?: number;
  resize?: 'none' | 'both' | 'horizontal' | 'vertical';
} & React.InputHTMLAttributes<HTMLTextAreaElement>;

export const TextArea: React.FC<Props> = ({
  children,
  className,
  name,
  label,
  description,
  registerOptions = {},
  handleErrorMessages = true,
  debouncedAutoSubmitForm,
  debouncedAutoSubmitTimeout,
  rows,
  resize = 'none',
  ...otherProps
}) => {
  const { register, formState } = useFormContext();

  const debouncedOnChange = React.useMemo(() => {
    if (!debouncedAutoSubmitForm || !debouncedAutoSubmitTimeout) return;

    return debounce(() => {
      const form = document.querySelector(debouncedAutoSubmitForm) as HTMLFormElement;
      if (!form) return console.warn(`Input: form with selector ${debouncedAutoSubmitForm} could not be found.`);
      form.requestSubmit();
    }, debouncedAutoSubmitTimeout);
  }, [debouncedAutoSubmitForm, debouncedAutoSubmitTimeout]);

  const errors = formState.errors[name];
  const errorContent = errors?.types ? Object.values(errors.types).map((text, index) => <small key={index}>{text}</small>) : null;

  return (
    <div className={cx('relative flex min-w-max flex-col', className)} data-testid="control-input">
      {label && (
        <label className="caption mb-2 block text-grey-label" htmlFor={`input-${name}`}>
          {label}
        </label>
      )}
      <textarea
        id={`input-${name}`}
        rows={rows}
        className={cx(
          'relative min-h-[44px] w-max min-w-full resize-none border border-grey-border py-2 pl-2 text-black focus:border-blue disabled:cursor-not-allowed',
          resize === 'none' && 'resize-none',
          resize === 'both' && 'resize',
          resize === 'horizontal' && 'resize-x',
          resize === 'vertical' && 'resize-y',
          errorContent && 'border-red-dark',
          otherProps.disabled && 'bg-gray-100',
        )}
        data-testid="textarea"
        {...register(name, { onChange: debouncedOnChange, ...registerOptions })}
        {...otherProps}
      />

      {description && <span className="text-grey">{description}</span>}
      {handleErrorMessages && errorContent && (
        <div data-testid="control-errors" className="red flex flex-col gap-1">
          {errorContent}
        </div>
      )}
      <div className="red mt-1">{children}</div>
    </div>
  );
};
